// 定义返回数据类型的泛型 T，默认值是 any
import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from 'axios'
import { clearToken, getToken } from '@/common/utils/token'
import JSONBig from 'json-bigint'
import { toRoutePath } from '@/common/utils/common'
import { useUserStore } from '@/stores/user'
import { useDictionaryStore } from '@/stores/dictionary'
import { useCommonStore } from '@/stores/common'
import { useTabStore } from '@/stores/tab'
import router from '@/router'
import { useSettingStore } from '@/stores/setting.ts'
import { UserTypeEnum } from '@/common/enums/system.ts'

export type PageRequest = {
    pageNumber: number | 1
    pageSize: number | 10
}

export type PageResponse<T = any> = {
    records: T[]
    totalSize: number | 0
}

export type ResultResponse<T = any> = {
    code: string
    message: string
    success: boolean | false
    tip: boolean | false
    traceId: string
    data: T
    timestamp: number | 0
}

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const http = axios.create({
    timeout: 50000,
    // withCredentials: true,
    headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'ut': UserTypeEnum.EMPLOYEE,
    },
})

if (VITE_API_BASE_URL) {
    http.defaults.baseURL = VITE_API_BASE_URL
}

http.defaults.transformResponse = [
    function (data) {
        return JSON.parse(JSON.stringify(JSONBig.parse(data)))
    },
]

const allowUrlList: string[] = ['/system/employee/token/get']

http.interceptors.request.use(
    function (config) {
        if (allowUrlList.includes(config.url as unknown as string)) {
            return config
        }
        const token = getToken()
        if (token) {
            if (!config.headers) {
                config.headers = {} as unknown as AxiosRequestHeaders
            }
            config.headers.Authorization = `Bearer ${token}`
        }
        config.headers = { ...config.headers } as unknown as AxiosRequestHeaders

        return config
    },
    function (error) {
        return Promise.reject(error)
    },
)

http.interceptors.response.use(
    async (response: AxiosResponse<ResultResponse>) => {
        const data = response.data
        if (!data.success) {
            // token 过期
            if (data?.code === 'R_0010000011' || data?.code === 'R_0010000012') {
                clearToken()
                const commonStore = useCommonStore()
                const dictionaryStore = useDictionaryStore()
                const userStore = useUserStore()
                const tabStore = useTabStore()
                const settingStore = useSettingStore()
                commonStore.reset()
                dictionaryStore.reset()
                userStore.reset()
                settingStore.reset()
                tabStore.reset()
                await router.replace(toRoutePath('/system/employee/login'))
            }
        }
        return response
    },
    error => {
        ElNotification.error({ message: '网络异常，请稍候重试~' })
        return Promise.reject(error)
    },
)

export { http }

class HttpUtil {
    static post = async <R = ResultResponse | any, D = any>(
        url: string,
        request?: D,
        config?: AxiosRequestConfig<D>,
    ): Promise<R> => {
        return http
            .post<R>(url, request || {}, config)
            .then((response: AxiosResponse) => {
                const result = response.data
                if (result?.success) {
                    if (result?.tip) {
                        ElNotification.success({ message: result?.message })
                    }
                } else {
                    if (result?.tip) {
                        ElNotification.error({ message: result?.message })
                    }
                }
                return result
            })
            .catch(error => {
                console.error(error)
                // ElNotification.error({message: '网络错误，请稍候重试~'})
                return Promise.reject(error)
            })
            .finally(() => {})
    }
}

export default HttpUtil
